/* Kohd layout: turns parsed sentences into exact SVG geometry.
   Word glyphs (Subroutine 1) sit on a 3x3 node grid; each sentence row
   (Subroutine 2) starts at a coupler and links the ground of each word to the
   charge of the next, with lexicon symbols placed on those links.
   All traces are routed octilinearly on a lattice so later traces run in
   parallel beside earlier ones instead of overlapping them. */

import { buildWordGlyph, Cell, Visit, WordGlyph } from './glyph';
import { parseKohd, GapItem, KohdSentence, Punctuation } from './parse';
import { Article, ConstructType } from './lexicon';
import { DIRS, routeOctilinear } from './router';

export interface Pt {
  x: number;
  y: number;
}

export const G = 100; // node spacing
export const LATTICE = 10;
export const NODE_R = 17;
export const RING_GAP = 7;
export const DOT_R = 3.6;
export const DOT_GAP = 9;
const NEAR_COST = 2;
/** Baseline of the optional word label, below the glyph. */
export const LABEL_Y = 2 * G + 90;
const GROUP_GAP = 7;
const CHARGE_LEN = 42;
const BAR_GAP = 8;
const WORD_SPACE = 80; // clear space between neighbouring words' reach
const ROW_H = 470;
const COUPLER_W = 50;
const NODE_CLEAR = 8;
const LOCAL_MIN = -9; // lattice bounds around a word (units of LATTICE)
const LOCAL_MAX = 29;

export const ringRadius = (ring: number) => NODE_R + RING_GAP * (ring - 1);

export interface RingEnd extends Pt {
  r: number;
}

export interface NodeShape extends Pt {
  col: number;
  row: number;
  rings: number;
  outerR: number;
}

export interface TraceShape {
  key: string;
  from: RingEnd;
  to: RingEnd;
  points: Pt[];
  groups: number[];
  letters: string[];
  dots: Pt[];
}

export interface BarShape extends Pt {
  /** Axis the bar caps; the bar itself is drawn across it. */
  dir: Pt;
}

export interface ChargeShape {
  node: Pt;
  port: Pt;
  dir: Pt;
  end: Pt;
  article?: Article;
  bar: BarShape;
}

export interface GroundShape {
  node: Pt;
  port: Pt;
  dir: Pt;
  lineEnd: Pt;
  end: Pt;
  groups: number[];
  letters: string[];
  dots: Pt[];
  bar: BarShape;
}

export interface NullShape extends Pt {
  stubEnd: Pt;
  tip: Pt;
}

export interface WordShape {
  key: string;
  text: string;
  origin: Pt;
  nodes: NodeShape[];
  traces: TraceShape[];
  charge: ChargeShape;
  ground: GroundShape;
  nullMod: NullShape | null;
  /** Baseline center of the optional word label: LABEL_Y below the word, lower if something runs under that spot. */
  label: Pt;
}

export type LinkItem = GapItem & {
  /** Arc length along the link where the item is centered. */
  s: number;
  at: Pt;
  angle: number;
  half: number;
};

export interface LinkShape {
  key: string;
  start: BarShape;
  end: BarShape | null;
  points: Pt[];
  items: LinkItem[];
}

export interface CouplerShape extends Pt {
  bars: Array<{ y: number; length: number }>;
  barHeight: number;
}

export interface EndShape {
  bar: BarShape | null;
  punctuation: Punctuation;
}

export interface RowShape {
  text: string;
  words: WordShape[];
  links: LinkShape[];
  coupler: CouplerShape;
  end: EndShape;
}

export interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface KohdLayout {
  rows: RowShape[];
  bounds: Bounds;
}

/* ── Geometry helpers ─────────────────────────────────────────────── */

const add = (a: Pt, b: Pt): Pt => ({ x: a.x + b.x, y: a.y + b.y });
const scale = (a: Pt, k: number): Pt => ({ x: a.x * k, y: a.y * k });
const unit = (d: number): Pt => {
  const v = DIRS[d];
  const len = Math.hypot(v.x, v.y);
  return { x: v.x / len, y: v.y / len };
};
const round2 = (v: number) => Math.round(v * 100) / 100;
const roundPt = (p: Pt): Pt => ({ x: round2(p.x), y: round2(p.y) });

export function distToSegment(p: Pt, a: Pt, b: Pt): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/** Drops points that lie on the straight line between their neighbours. */
export function simplify(points: Pt[]): Pt[] {
  const out: Pt[] = [];
  for (const p of points) {
    if (out.length > 0 && Math.abs(out[out.length - 1].x - p.x) < 1e-6 && Math.abs(out[out.length - 1].y - p.y) < 1e-6) continue;
    while (out.length >= 2) {
      const a = out[out.length - 2];
      const b = out[out.length - 1];
      const cross = (b.x - a.x) * (p.y - b.y) - (b.y - a.y) * (p.x - b.x);
      const dot = (b.x - a.x) * (p.x - b.x) + (b.y - a.y) * (p.y - b.y);
      if (Math.abs(cross) < 1e-6 && dot >= 0) out.pop();
      else break;
    }
    out.push(p);
  }
  return out.map(roundPt);
}

export function pathLength(points: Pt[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i++) total += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
  return total;
}

/** Point and direction angle (radians) at arc length s along a polyline. */
export function pointAt(points: Pt[], s: number): { at: Pt; angle: number } {
  let walked = 0;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    if (len === 0) continue;
    if (walked + len >= s || i === points.length - 1) {
      const t = Math.max(0, Math.min(1, (s - walked) / len));
      return { at: roundPt({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t }), angle: Math.atan2(b.y - a.y, b.x - a.x) };
    }
    walked += len;
  }
  return { at: points[0] ?? { x: 0, y: 0 }, angle: 0 };
}

/** The part of a polyline between arc lengths a and b (used to break links around symbols). */
export function slicePath(points: Pt[], a: number, b: number): Pt[] {
  const out = [pointAt(points, a).at];
  let walked = 0;
  for (let i = 1; i < points.length; i++) {
    walked += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
    if (walked > a && walked < b) out.push(points[i]);
  }
  out.push(pointAt(points, b).at);
  return out;
}

/** Subnode dots: one group per letter, in letter order from the path start. */
export function placeDots(points: Pt[], groups: number[], startPad = 10, endPad = 10): Pt[] {
  if (groups.length === 0 || points.length < 2) return [];
  const length = pathLength(points);
  const usable = Math.max(0, length - startPad - endPad);
  const slot = usable / groups.length;
  const dots: Pt[] = [];
  groups.forEach((count, i) => {
    const gap = count > 1 ? Math.min(DOT_GAP, (slot * 0.85) / (count - 1)) : 0;
    const center = startPad + slot * (i + 0.5);
    for (let k = 0; k < count; k++) {
      const s = Math.max(0, Math.min(length, center + (k - (count - 1) / 2) * gap));
      dots.push(pointAt(points, s).at);
    }
  });
  return dots;
}

function hashString(text: string): number {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) h = Math.imul(h ^ text.charCodeAt(i), 16777619);
  return h >>> 0;
}

function seededRandom(seed: number) {
  let s = seed || 1;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

/* ── Obstacles and occupancy shared by every route in a row ─────────── */

interface Disc extends Pt { r: number }
interface Bar { a: Pt; b: Pt; half: number }

class RouteField {
  discs: Disc[] = [];
  bars: Bar[] = [];
  private points = new Map<string, number>();
  private edges = new Map<string, number>();
  private mids = new Map<string, number>();
  private near = new Map<string, number>();

  addDisc(c: Pt, r: number) { this.discs.push({ ...c, r }); }
  addBar(a: Pt, b: Pt, half: number) { this.bars.push({ a, b, half }); }

  blocked(p: Pt, ignore: Disc[] = []): boolean {
    for (const d of this.discs) {
      if (ignore.includes(d)) continue;
      if (Math.hypot(p.x - d.x, p.y - d.y) < d.r) return true;
    }
    return this.bars.some((b) => distToSegment(p, b.a, b.b) < b.half);
  }

  occupy(lattice: Pt[]) {
    const bump = (m: Map<string, number>, k: string) => m.set(k, (m.get(k) ?? 0) + 1);
    lattice.forEach((p, i) => {
      bump(this.points, `${p.x},${p.y}`);
      [[1, 0], [-1, 0], [0, 1], [0, -1]].forEach(([dx, dy]) => bump(this.near, `${p.x + dx},${p.y + dy}`));
      if (i === 0) return;
      const q = lattice[i - 1];
      bump(this.edges, edgeKey(q, p));
      if (q.x !== p.x && q.y !== p.y) bump(this.mids, `${q.x + p.x},${q.y + p.y}`);
    });
  }

  // Crossing another route costs; running right beside one costs a little, so parallel lines keep a gap.
  pointCost = (x: number, y: number) => 6 * (this.points.get(`${x},${y}`) ?? 0) + NEAR_COST * (this.near.get(`${x},${y}`) ?? 0);
  edgeCost = (ax: number, ay: number, bx: number, by: number) => {
    const a = { x: ax, y: ay };
    const b = { x: bx, y: by };
    let cost = 80 * (this.edges.get(edgeKey(a, b)) ?? 0);
    if (ax !== bx && ay !== by) cost += 6 * (this.mids.get(`${ax + bx},${ay + by}`) ?? 0);
    return cost;
  };
}

const edgeKey = (a: Pt, b: Pt) => (a.x < b.x || (a.x === b.x && a.y < b.y) ? `${a.x},${a.y}|${b.x},${b.y}` : `${b.x},${b.y}|${a.x},${a.y}`);

/** First lattice point on the ray from `center` along direction d that is at least `minDist` away. */
function latticeOnRay(center: Pt, d: number, minDist: number): Pt {
  const step = Math.hypot(DIRS[d].x, DIRS[d].y) * LATTICE;
  const k = Math.max(1, Math.ceil(minDist / step));
  return { x: center.x + DIRS[d].x * LATTICE * k, y: center.y + DIRS[d].y * LATTICE * k };
}

interface RouteEnds {
  startPoint: Pt;
  startLattice: Pt;
  startDir: number;
  goalLattice: Pt;
  goalDir: number;
  endPoint: Pt;
  /** Lattice ends closer to the bars, tried when the first route is boxed in (e.g. a charge facing a used neighbour). */
  nearStart?: Pt;
  nearGoal?: Pt;
}

function route(field: RouteField, ends: RouteEnds, bounds: Bounds, ignore: Disc[]): Pt[] {
  const toL = (p: Pt) => ({ x: Math.round(p.x / LATTICE), y: Math.round(p.y / LATTICE) });
  const s = toL(ends.startLattice);
  const g = toL(ends.goalLattice);
  const blocked = new Map<string, boolean>(); // the field does not change during one route
  const attempt = (start: Pt, goal: Pt) => routeOctilinear({
    start,
    startDir: ends.startDir,
    goal,
    goalDir: ends.goalDir,
    bounds: {
      minX: Math.floor(bounds.minX / LATTICE), minY: Math.floor(bounds.minY / LATTICE),
      maxX: Math.ceil(bounds.maxX / LATTICE), maxY: Math.ceil(bounds.maxY / LATTICE),
    },
    isBlocked: (x, y) => {
      const k = `${x},${y}`;
      if (!blocked.has(k)) blocked.set(k, field.blocked({ x: x * LATTICE, y: y * LATTICE }, ignore));
      return blocked.get(k)!;
    },
    pointCost: field.pointCost,
    edgeCost: field.edgeCost,
  });
  const lattice = attempt(s, g)
    ?? (ends.nearStart || ends.nearGoal ? attempt(toL(ends.nearStart ?? ends.startLattice), toL(ends.nearGoal ?? ends.goalLattice)) : null)
    ?? fallbackLattice(s, g);
  field.occupy(lattice);
  return simplify([ends.startPoint, ...lattice.map((p) => ({ x: p.x * LATTICE, y: p.y * LATTICE })), ends.endPoint]);
}

/** Diagonal-then-straight path used only if no clear route exists. */
function fallbackLattice(a: Pt, b: Pt): Pt[] {
  const out = [a];
  let { x, y } = a;
  while (x !== b.x || y !== b.y) {
    x += Math.sign(b.x - x);
    y += Math.sign(b.y - y);
    out.push({ x, y });
  }
  return out;
}

/* ── Word glyphs ──────────────────────────────────────────────────── */

function octantToward(from: Pt, to: Pt): number {
  const angle = Math.atan2(to.y - from.y, to.x - from.x);
  return ((Math.round(angle / (Math.PI / 4)) % 8) + 8) % 8;
}

function choosePort(ideal: number, used: Set<number>): number {
  const order = Array.from({ length: 8 }, (_, d) => d)
    .filter((d) => !used.has(d))
    .sort((a, b) => {
      const ta = Math.min(Math.abs(a - ideal), 8 - Math.abs(a - ideal));
      const tb = Math.min(Math.abs(b - ideal), 8 - Math.abs(b - ideal));
      return ta - tb || a - b;
    });
  return order[0] ?? ideal;
}

/* Indicator preference by direction (index into DIRS). The charge takes the link arriving
   from the left and the ground sends it on to the right; up and down keep words compact.
   As in the rulebook, indicators always point straight. */
const ORTHOGONAL = [0, 2, 4, 6];
const CHARGE_PREF: Record<number, number> = { 0: -2, 2: 0.2, 4: 0.8, 6: 1 };
const GROUND_PREF: Record<number, number> = { 0: 0.8, 2: 1, 4: -2, 6: 0.2 };
// A node boxed in on all four sides points at a neighbour, facing where its link comes from or goes to.
const CHARGE_CRAMPED_PREF: Record<number, number> = { 0: -2, 2: 0.2, 4: 1, 6: 0.5 };
const GROUND_CRAMPED_PREF: Record<number, number> = { 0: 1, 2: 0.5, 4: -2, 6: 0.2 };
const CRAMPED_COST = 10;
const CRAMPED_LEN = 26; // then the line stops short, in the gap before that neighbour
const INDICATOR_CLEAR = 16; // traces keep this far from a charge or ground line

/** Indicators point out of the glyph and with the sentence flow; at a neighbour or the null corner only when nothing else is free. */
function chooseIndicatorDir(cell: Cell, used: Set<number>, occupied: Set<string>, pref: Record<number, number>, crampedPref: Record<number, number>): number {
  const out = { x: cell.col - 1, y: cell.row - 1 };
  let best = ORTHOGONAL[0];
  let bestScore = -Infinity;
  for (const d of ORTHOGONAL) {
    if (used.has(d)) continue;
    const v = DIRS[d];
    const cramped = occupied.has(`${cell.col + v.x},${cell.row + v.y}`);
    const score = 2 * (v.x * out.x + v.y * out.y) + (cramped ? crampedPref[d] - CRAMPED_COST : pref[d]);
    if (score > bestScore) { bestScore = score; best = d; }
  }
  return best;
}

const cellKey = (c: Cell) => `${c.col},${c.row}`;
const groundLength = (last: Visit) => Math.max(28, 22 + (last.dots.reduce((a, b) => a + b, 0) - 1) * DOT_GAP + (last.dots.length - 1) * GROUP_GAP);

/** A word's glyph, indicator directions and lengths, and horizontal reach from its origin, decided before it is placed. */
interface WordPlan {
  glyph: WordGlyph;
  chargeDir: number;
  groundDir: number;
  chargeLen: number;
  groundLen: number;
  left: number;
  right: number;
}

function planWord(text: string): WordPlan {
  const glyph = buildWordGlyph(text);
  const first = glyph.visits[0];
  const last = glyph.visits[glyph.visits.length - 1];
  const occupied = new Set(glyph.nodes.map(cellKey));
  if (glyph.nullCorner) occupied.add(cellKey(glyph.nullCorner));
  const chargeDir = chooseIndicatorDir(first, new Set(), occupied, CHARGE_PREF, CHARGE_CRAMPED_PREF);
  const groundDir = chooseIndicatorDir(last, cellKey(first) === cellKey(last) ? new Set([chargeDir]) : new Set(), occupied, GROUND_PREF, GROUND_CRAMPED_PREF);
  const ahead = (c: Cell, d: number) => occupied.has(`${c.col + DIRS[d].x},${c.row + DIRS[d].y}`);
  const chargeLen = ahead(first, chargeDir) ? CRAMPED_LEN : CHARGE_LEN;
  const groundLen = ahead(last, groundDir) ? CRAMPED_LEN - 10 : groundLength(last); // the ground adds a 10-unit tail
  const xs = glyph.nodes.flatMap((n) => [n.col * G - ringRadius(n.rings), n.col * G + ringRadius(n.rings)]);
  const reach = (c: Visit, d: number, len: number) => c.col * G + DIRS[d].x * (ringRadius(c.ring) + len);
  const chargeX = reach(first, chargeDir, chargeLen + BAR_GAP);
  const groundX = reach(last, groundDir, groundLen + 10 + BAR_GAP);
  xs.push(chargeX - 10, chargeX + 10, groundX - 10, groundX + 10);
  if (glyph.nullCorner) xs.push(glyph.nullCorner.col * G - 12, glyph.nullCorner.col * G + 12);
  return { glyph, chargeDir, groundDir, chargeLen, groundLen, left: Math.min(...xs), right: Math.max(...xs) };
}

function layoutWord(plan: WordPlan, key: string, origin: Pt, field: RouteField, article?: Article): WordShape {
  const { glyph, chargeDir, groundDir, chargeLen, groundLen } = plan;
  const text = glyph.word;
  const center = (c: Cell): Pt => ({ x: origin.x + c.col * G, y: origin.y + c.row * G });
  const nodes: NodeShape[] = glyph.nodes.map((n) => ({ ...center(n), col: n.col, row: n.row, rings: n.rings, outerR: ringRadius(n.rings) }));
  const nodeAt = (c: Cell) => nodes.find((n) => n.col === c.col && n.row === c.row)!;
  const ports = new Map<string, Set<number>>();
  const portsOf = (c: Cell) => {
    const k = cellKey(c);
    if (!ports.has(k)) ports.set(k, new Set());
    return ports.get(k)!;
  };

  const discs = nodes.map((n) => ({ x: n.x, y: n.y, r: n.outerR + NODE_CLEAR }));
  discs.forEach((d) => field.addDisc(d, d.r));
  const ownDisc = (n: NodeShape) => field.discs.find((d) => d.x === n.x && d.y === n.y && d.r === n.outerR + NODE_CLEAR)!;

  const visits = glyph.visits;
  const first = visits[0];
  const last = visits[visits.length - 1];

  // Charge indicator (resistor) on the first visit's ring.
  portsOf(first).add(chargeDir);
  const chargeNode = nodeAt(first);
  const cu = unit(chargeDir);
  const chargePort = add(chargeNode, scale(cu, ringRadius(first.ring)));
  const chargeEnd = add(chargePort, scale(cu, chargeLen));
  const chargeBar = add(chargeEnd, scale(cu, BAR_GAP));
  field.addBar(chargePort, chargeBar, INDICATOR_CLEAR);

  // Ground indicator on the last visit's ring; the last letters' dots ride its line.
  portsOf(last).add(groundDir);
  const groundNode = nodeAt(last);
  const gu = unit(groundDir);
  const groundPort = add(groundNode, scale(gu, ringRadius(last.ring)));
  const groundLineEnd = add(groundPort, scale(gu, groundLen));
  const groundEnd = add(groundLineEnd, scale(gu, 10));
  const groundBar = add(groundEnd, scale(gu, BAR_GAP));
  field.addBar(groundPort, groundBar, INDICATOR_CLEAR);

  // Null modifier: a crossed circle in the remote corner with a stub toward the grid center.
  let nullMod: NullShape | null = null;
  if (glyph.nullCorner) {
    const c = center(glyph.nullCorner);
    const mid = { x: origin.x + G, y: origin.y + G };
    const len = Math.hypot(mid.x - c.x, mid.y - c.y);
    const v = { x: (mid.x - c.x) / len, y: (mid.y - c.y) / len };
    nullMod = { ...c, stubEnd: roundPt(add(c, scale(v, 34))), tip: roundPt(add(c, scale(v, 38.5))) };
    field.addDisc(c, 12 + NODE_CLEAR);
    field.addBar(c, nullMod.tip, 9);
  }

  // Traces between consecutive visits, routed in letter order.
  const bounds: Bounds = {
    minX: origin.x + LOCAL_MIN * LATTICE, minY: origin.y + LOCAL_MIN * LATTICE,
    maxX: origin.x + LOCAL_MAX * LATTICE, maxY: origin.y + LOCAL_MAX * LATTICE,
  };
  const traces: TraceShape[] = [];
  for (let i = 1; i < visits.length; i++) {
    const a: Visit = visits[i - 1];
    const b: Visit = visits[i];
    const na = nodeAt(a);
    const nb = nodeAt(b);
    const da = choosePort(octantToward(na, nb), portsOf(a));
    portsOf(a).add(da);
    const db = choosePort(octantToward(nb, na), portsOf(b));
    portsOf(b).add(db);
    const ra = ringRadius(a.ring);
    const rb = ringRadius(b.ring);
    const points = route(field, {
      startPoint: add(na, scale(unit(da), ra)),
      startLattice: latticeOnRay(na, da, na.outerR + 6),
      startDir: da,
      goalLattice: latticeOnRay(nb, db, nb.outerR + 6),
      goalDir: (db + 4) % 8,
      endPoint: add(nb, scale(unit(db), rb)),
    }, bounds, [ownDisc(na), ownDisc(nb)]);
    traces.push({
      key: `${key}:t${i}`,
      from: { x: na.x, y: na.y, r: ra },
      to: { x: nb.x, y: nb.y, r: rb },
      points,
      groups: a.dots,
      letters: a.letters,
      dots: placeDots(points, a.dots),
    });
  }

  return {
    key,
    text,
    origin,
    nodes,
    traces,
    charge: { node: { x: chargeNode.x, y: chargeNode.y }, port: roundPt(chargePort), dir: cu, end: roundPt(chargeEnd), article, bar: { ...roundPt(chargeBar), dir: cu } },
    ground: {
      node: { x: groundNode.x, y: groundNode.y },
      port: roundPt(groundPort), dir: gu, lineEnd: roundPt(groundLineEnd), end: roundPt(groundEnd),
      groups: last.dots, letters: last.letters,
      dots: placeDots([groundPort, groundLineEnd], last.dots, 8, 6),
      bar: { ...roundPt(groundBar), dir: gu },
    },
    nullMod,
    label: { x: origin.x + G, y: origin.y + LABEL_Y },
  };
}

/* ── Sentence rows ────────────────────────────────────────────────── */

/** Half length each lexicon symbol takes along its link. */
export const ITEM_HALF: Record<ConstructType | 'comma', number> = {
  and: 34, or: 34, true: 26, false: 26, because: 26, so: 26, if: 20, ifThen: 24,
  thereIs: 18, unique: 18, fromTo: 30, you: 14, me: 14, youAll: 14, we: 14, them: 22, their: 22, comma: 6,
};

const itemHalf = (item: GapItem) => ITEM_HALF[item.kind === 'comma' ? 'comma' : item.type];
const gapWidth = (items: GapItem[]) => items.reduce((w, item) => w + 2 * itemHalf(item) + 26, 0);
const snap = (v: number) => Math.round(v / LATTICE) * LATTICE;

/** Spreads items along the straight runs of a link, away from its bends. */
export function placeItems(points: Pt[], items: GapItem[]): LinkItem[] {
  if (items.length === 0) return [];
  const maxHalf = Math.max(...items.map(itemHalf));
  const runs: Array<[number, number]> = [];
  let walked = 0;
  for (let i = 1; i < points.length; i++) {
    const len = Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
    const pad = maxHalf + 6;
    if (len > 2 * pad) runs.push([walked + pad, walked + len - pad]);
    walked += len;
  }
  const usable = runs.reduce((sum, [a, b]) => sum + (b - a), 0);
  const total = pathLength(points);
  const positions = items.map((_, i) => {
    if (usable <= 0) return (total * (i + 1)) / (items.length + 1);
    let target = (usable * (i + 0.5)) / items.length;
    for (const [a, b] of runs) {
      if (target <= b - a) return a + target;
      target -= b - a;
    }
    return total / 2;
  });
  return items.map((item, i) => ({ ...item, s: round2(positions[i]), ...pointAt(points, positions[i]), half: itemHalf(item) }));
}

/** Manual word offsets, keyed by word key; links re-route around the moved glyphs. */
export type KohdMoves = Record<string, Pt>;

function layoutRow(sentence: KohdSentence, rowIndex: number, text: string, moves: KohdMoves): RowShape {
  const field = new RouteField();
  const y0 = rowIndex * ROW_H;
  const random = seededRandom(hashString(text));

  const barHeight = 8;
  const bars = Array.from({ length: 16 }, (_, i) => ({ y: y0 + i * 12.5, length: Math.round(18 + random() * 32) }));
  const coupler: CouplerShape = { x: COUPLER_W, y: y0, bars, barHeight };
  field.addBar({ x: 0, y: y0 - 6 }, { x: 0, y: y0 + 206 }, COUPLER_W + 4);

  // Words pack by their real reach (nodes and indicators), a clear space apart.
  const words: WordShape[] = [];
  const seen = new Map<string, number>();
  let edge = COUPLER_W;
  sentence.words.forEach((w, i) => {
    const n = (seen.get(w.text) ?? 0) + 1;
    seen.set(w.text, n);
    const key = `${rowIndex}:${w.text}#${n}`;
    const move = moves[key] ?? { x: 0, y: 0 };
    const plan = planWord(w.text);
    const x = snap(edge + WORD_SPACE + gapWidth(sentence.gaps[i]) - plan.left);
    words.push(layoutWord(plan, key, { x: x + move.x, y: y0 + move.y }, field, w.article));
    edge = x + plan.right;
  });

  const links: LinkShape[] = [];
  const couplerStart: BarShape = { x: COUPLER_W, y: y0 + G, dir: { x: 1, y: 0 } };
  const trailing = sentence.gaps[sentence.words.length] ?? [];

  const linkBounds = (a: Pt, b: Pt): Bounds => ({
    minX: Math.min(a.x, b.x) - 160, minY: Math.min(y0, a.y, b.y) - 140,
    maxX: Math.max(a.x, b.x) + 160, maxY: Math.max(y0 + 2 * G, a.y, b.y) + 140,
  });
  const fromGround = (w: WordShape) => {
    const d = octantToward({ x: 0, y: 0 }, w.ground.dir);
    const dist = Math.hypot(w.ground.bar.x - w.ground.node.x, w.ground.bar.y - w.ground.node.y);
    return { bar: w.ground.bar, lattice: latticeOnRay(w.ground.node, d, dist + 6), near: latticeOnRay(w.ground.node, d, dist + 1), dir: d };
  };
  const toCharge = (w: WordShape) => {
    const d = octantToward({ x: 0, y: 0 }, w.charge.dir);
    const dist = Math.hypot(w.charge.bar.x - w.charge.node.x, w.charge.bar.y - w.charge.node.y);
    return { bar: w.charge.bar, lattice: latticeOnRay(w.charge.node, d, dist + 6), near: latticeOnRay(w.charge.node, d, dist + 1), dir: d };
  };
  type LinkStart = { bar: BarShape; lattice: Pt; near?: Pt; dir: number };
  type LinkEnd = { bar: BarShape | null; point: Pt; lattice: Pt; near?: Pt; goalDir: number };
  const addLink = (key: string, start: LinkStart, end: LinkEnd, items: GapItem[]) => {
    const points = route(field, {
      startPoint: start.bar, startLattice: start.lattice, startDir: start.dir, nearStart: start.near,
      goalLattice: end.lattice, goalDir: end.goalDir, endPoint: end.point, nearGoal: end.near,
    }, linkBounds(start.lattice, end.lattice), []);
    links.push({ key, start: start.bar, end: end.bar, points, items: placeItems(points, items) });
  };

  const couplerFrom = { bar: couplerStart, lattice: { x: snap(COUPLER_W + 10), y: y0 + G }, dir: 0 };
  if (words.length === 0) {
    const endBar: BarShape = { x: snap(COUPLER_W + 120 + gapWidth(sentence.gaps[0])), y: y0 + G, dir: { x: 1, y: 0 } };
    addLink(`${rowIndex}:link0`, couplerFrom, { bar: endBar, point: endBar, lattice: { x: endBar.x - LATTICE, y: endBar.y }, goalDir: 0 }, sentence.gaps[0]);
    return { text, words, links, coupler, end: { bar: endBar, punctuation: sentence.punctuation } };
  }

  words.forEach((w, i) => {
    const target = toCharge(w);
    const from = i === 0 ? couplerFrom : fromGround(words[i - 1]);
    const key = i === 0 ? `${rowIndex}:coupler>${w.key}` : `${words[i - 1].key}>${w.key}`;
    addLink(key, from, { bar: target.bar, point: target.bar, lattice: target.lattice, near: target.near, goalDir: (target.dir + 4) % 8 }, sentence.gaps[i]);
  });

  const lastWord = words[words.length - 1];
  let endBar: BarShape | null = lastWord.ground.bar;
  if (trailing.length > 0) {
    const start = fromGround(lastWord);
    const tail: BarShape = { x: snap(lastWord.origin.x + 2 * G + 120 + gapWidth(trailing)), y: snap(start.lattice.y), dir: { x: 1, y: 0 } };
    addLink(`${lastWord.key}>end`, start, { bar: tail, point: tail, lattice: { x: tail.x - LATTICE, y: tail.y }, goalDir: 0 }, trailing);
    endBar = tail;
  }

  return { text, words, links, coupler, end: { bar: endBar, punctuation: sentence.punctuation } };
}

/* ── Word labels ──────────────────────────────────────────────────── */

const LABEL_CLEAR = 30; // baseline gap under a line, so the label's caps clear it

/** Lowest y a polyline (or a single point) reaches between x = lo and x = hi; -Infinity if it stays outside. */
function lowestIn(points: Pt[], lo: number, hi: number): number {
  let low = -Infinity;
  points.forEach((b, i) => {
    const a = i === 0 ? b : points[i - 1];
    const x0 = Math.max(lo, Math.min(a.x, b.x));
    const x1 = Math.min(hi, Math.max(a.x, b.x));
    if (x0 > x1) return;
    const yAt = (x: number) => (a.x === b.x ? Math.max(a.y, b.y) : a.y + ((b.y - a.y) * (x - a.x)) / (b.x - a.x));
    low = Math.max(low, yAt(x0), yAt(x1));
  });
  return low;
}

/** Drops each word label below any trace, link or part that runs under its default spot. */
function placeLabels(row: RowShape): RowShape {
  const paths: Pt[][] = [
    ...row.links.map((l) => l.points),
    ...row.words.flatMap((w) => [
      ...w.traces.map((t) => t.points),
      [w.charge.node, w.charge.port, w.charge.end],
      [w.ground.node, w.ground.port, w.ground.lineEnd, w.ground.end],
      [{ x: w.charge.bar.x, y: w.charge.bar.y + 9 }],
      [{ x: w.ground.bar.x, y: w.ground.bar.y + 9 }],
      ...w.nodes.map((n) => [{ x: n.x - n.outerR, y: n.y + n.outerR }, { x: n.x + n.outerR, y: n.y + n.outerR }]),
      ...(w.nullMod ? [[{ x: w.nullMod.x, y: w.nullMod.y + 14 }]] : []),
    ]),
  ];
  const words = row.words.map((w) => {
    // shortcut: label width is estimated for the 18px label font; measure the text if labels change size
    const half = w.text.length * 7 + 12;
    const low = Math.max(...paths.map((p) => lowestIn(p, w.label.x - half, w.label.x + half)));
    return { ...w, label: { x: w.label.x, y: Math.max(w.label.y, low + LABEL_CLEAR) } };
  });
  return { ...row, words };
}

/* ── Bounds and entry point ───────────────────────────────────────── */

function rowPoints(row: RowShape): Pt[] {
  const pts: Pt[] = [{ x: 0, y: row.coupler.y - 10 }, { x: 0, y: row.coupler.y + 210 }];
  row.words.forEach((w) => {
    w.nodes.forEach((n) => pts.push({ x: n.x - n.outerR, y: n.y - n.outerR }, { x: n.x + n.outerR, y: n.y + n.outerR }));
    w.traces.forEach((t) => pts.push(...t.points));
    pts.push(w.charge.bar, w.ground.bar);
    pts.push({ x: w.label.x, y: w.label.y + 6 });
    if (w.nullMod) pts.push({ x: w.nullMod.x - 14, y: w.nullMod.y - 14 }, { x: w.nullMod.x + 14, y: w.nullMod.y + 14 });
  });
  row.links.forEach((l) => pts.push(...l.points));
  if (row.end.bar) pts.push(add(row.end.bar, scale(row.end.bar.dir, 30)));
  return pts;
}

export function layoutKohd(text: string, moves: KohdMoves = {}): KohdLayout {
  const sentences = parseKohd(text);
  const rows = sentences.map((s, i) => placeLabels(layoutRow(s, i, `${i}:${text}`, moves)));
  const pts = rows.flatMap(rowPoints);
  const bounds: Bounds = pts.length === 0
    ? { minX: 0, minY: 0, maxX: 400, maxY: 300 }
    : {
      minX: Math.min(...pts.map((p) => p.x)) - 40,
      minY: Math.min(...pts.map((p) => p.y)) - 70,
      maxX: Math.max(...pts.map((p) => p.x)) + 40,
      maxY: Math.max(...pts.map((p) => p.y)) + 40,
    };
  return { rows, bounds };
}
